import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { MibButtonTogglesComponent, MibButtonTogglesSegment } from '@medinbox/angular';

/** One choice of the row: the value it stands for, and the wording it shows. */
export interface SegmentedOption<TValue extends string> {
  value: TValue;
  label: string;
}

/**
 * A row of joined segments, for picking one value out of a few named ones, drawn by the library's
 * segmented control.
 *
 * The component is controlled. It never changes {@link value}, it reports the pick, so the row and
 * the state it drives cannot drift apart.
 *
 * ```html
 * <mai-segmented-picker
 *   legend="Text size"
 *   [options]="textSizes"
 *   [value]="settings().textSize"
 *   (valueChange)="save({ textSize: $event })"></mai-segmented-picker>
 * ```
 */
@Component({
  selector: 'mai-segmented-picker',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [MibButtonTogglesComponent],
  templateUrl: './segmented-picker.html',
})
export class SegmentedPicker<TValue extends string> {
  /** Accessible name of the group, rendered above the row. */
  readonly legend = input.required<string>();
  readonly options = input.required<readonly SegmentedOption<TValue>[]>();
  readonly value = input.required<TValue>();
  /** Locks the row while the host's write is in flight. */
  readonly pending = input(false);

  /** Emits the value picked. A pick on the value already held reports nothing. */
  readonly valueChange = output<TValue>();

  protected readonly segments = computed<MibButtonTogglesSegment[]>(() =>
    this.options().map(({ value, label }) => ({ id: value, label })),
  );

  /* The group speaks a list even when only one segment can be on. */
  protected readonly active = computed(() => [this.value()]);

  protected pick(picked: readonly string[]): void {
    const [value] = picked;
    if (!value || value === this.value()) {
      return;
    }
    this.valueChange.emit(value as TValue);
  }
}
